/**
 * Data normalization and utility helpers for DataContext
 * Provides pure functions for deep cloning, ID normalization, date normalization,
 * deduplication, and conflict resolution with optimistic update support.
 */

// ============================================================================
// Debug Flags
// ============================================================================

export const DEBUG_DATA_RACES = process.env.NODE_ENV !== 'production';
export const DEBUG_PERF = process.env.NODE_ENV !== 'production' && 
  typeof localStorage !== 'undefined' && 
  localStorage.getItem('dh.debug.perf') === '1';

// ============================================================================
// Chaos Mode Switches (for dev testing only)
// ============================================================================

export const FAIL_NEXT_ASSIGNMENT_UPDATE = () => 
  typeof localStorage !== 'undefined' && 
  localStorage.getItem('dh.chaos.failNext') === '1';

export const SIMULATE_OUT_OF_ORDER = () => 
  typeof localStorage !== 'undefined' && 
  localStorage.getItem('dh.chaos.outOfOrder') === '1';

export const CHAOS_SOFT_DELETE_ID = () => 
  typeof localStorage !== 'undefined' ? 
  localStorage.getItem('dh.chaos.softDeleteId') : null;

// ============================================================================
// Deep Clone Helper
// ============================================================================

/**
 * Deep clone an object using structuredClone if available, JSON fallback
 * @param {any} obj - Object to clone
 * @returns {any} Deep cloned object
 */
export function deepClone(obj) {
  if (obj == null) return obj;
  if (typeof structuredClone === 'function') {
    return structuredClone(obj);
  }
  // Fallback for plain JSON-ish objects
  return JSON.parse(JSON.stringify(obj));
}

// ============================================================================
// ID Normalization Helpers
// ============================================================================

/**
 * Get first non-null value from object using multiple possible keys
 * @param {Object} node - Object to search
 * @param {string[]} keys - Array of possible key names
 * @returns {any} First non-null value found
 */
function getFirst(node, keys) {
  if (!node) return null;
  for (const k of keys) {
    if (node[k] != null) return node[k];
  }
  return null;
}

/**
 * Normalize endeavor (course) ID from various possible field names
 */
export function normEndeavorId(node) {
  return getFirst(node, ['endeavorId', 'endeavorID', 'endeavor_id', 'courseId', 'course_id']);
}

/**
 * Normalize event ID from various possible field names
 */
export function normEventId(node) {
  return getFirst(node, ['eventId', 'eventID', 'event_id', 'id']);
}

/**
 * Normalize assignment ID from various possible field names
 */
export function normAssignmentId(node) {
  return getFirst(node, ['assignmentId', 'assignmentID', 'assignment_id', 'id']);
}

/**
 * Normalize document ID from various possible field names
 */
export function normDocumentId(node) {
  return getFirst(node, ['documentId', 'documentID', 'document_id', 'id']);
}

/**
 * Normalize course ID from various possible field names
 */
export function normCourseId(node) {
  return getFirst(node, ['courseId', 'id', 'endeavorId', 'endeavorID']);
}

// ============================================================================
// Date Normalization Helpers
// ============================================================================

/**
 * Normalize assignment due date to canonical 'dueAt' field
 * @param {Object} assignment - Assignment object
 * @returns {Date|string|null} Normalized due date
 */
export function normDueDate(assignment) {
  if (!assignment) return null;
  return getFirst(assignment, ['dueAt', 'dueDatetime', 'dueDate', 'due_at', 'due_date', 'deadline']);
}

/**
 * Normalize event start date to canonical 'startAt' field
 * @param {Object} event - Event object
 * @returns {Date|string|null} Normalized start date
 */
export function normStartDate(event) {
  if (!event) return null;
  return getFirst(event, ['startAt', 'startDatetime', 'startDateTime', 'start_datetime', 'startDate', 'start_at', 'start_date', 'startTime', 'start_time', 'eventDate', 'event_date', 'date']);
}

// ============================================================================
// Date Helper Functions
// ============================================================================

/**
 * Get date range for common time periods
 * @param {string} type - Range type: 'week', 'month', 'today', 'tomorrow'
 * @returns {{ start: Date, end: Date }} Date range
 */
export function getDateRange(type) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  switch (type) {
    case 'today':
      return {
        start: today,
        end: new Date(today.getTime() + 24 * 60 * 60 * 1000)
      };
    
    case 'tomorrow':
      const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
      return {
        start: tomorrow,
        end: new Date(tomorrow.getTime() + 24 * 60 * 60 * 1000)
      };
    
    case 'week':
      return {
        start: today,
        end: new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)
      };
    
    case 'month':
      return {
        start: today,
        end: new Date(today.getFullYear(), today.getMonth() + 1, today.getDate())
      };
    
    default:
      return { start: today, end: new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000) };
  }
}

/**
 * Check if a date is in the future
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if date is upcoming
 */
export function isUpcoming(date) {
  if (!date) return false;
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj > new Date();
}

/**
 * Check if a date is in the past
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if date is past
 */
export function isPast(date) {
  if (!date) return false;
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj < new Date();
}

/**
 * Check if a date is within a range
 * @param {Date|string} date - Date to check
 * @param {Date|string} start - Range start
 * @param {Date|string} end - Range end
 * @returns {boolean} True if date is in range
 */
export function isInRange(date, start, end) {
  if (!date) return false;
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const startObj = typeof start === 'string' ? new Date(start) : start;
  const endObj = typeof end === 'string' ? new Date(end) : end;
  return dateObj >= startObj && dateObj <= endObj;
}

// ============================================================================
// Deduplication Helper
// ============================================================================

/**
 * Deduplicate array of items by ID, returning plain object { [id]: entity }
 * Logs duplicate IDs in dev mode for debugging
 * @param {Array} items - Array of items to deduplicate
 * @param {string} source - Source identifier for logging (e.g., cache key)
 * @returns {Object} Plain object mapping ID to entity
 */
export function dedupeById(items, source = 'unknown') {
  if (!Array.isArray(items)) return {};
  
  const result = {};
  const duplicates = [];
  
  for (const item of items) {
    if (!item) continue;
    
    // Try to get ID from various possible fields
    const id = normAssignmentId(item) || normEventId(item) || normDocumentId(item) || normCourseId(item);
    
    if (!id) {
      if (DEBUG_DATA_RACES) {
        console.warn('[dataUtils] Item missing ID', { item, source });
      }
      continue;
    }
    
    if (result[id]) {
      duplicates.push(id);
    }
    
    result[id] = item;
  }
  
  if (DEBUG_DATA_RACES && duplicates.length > 0) {
    console.warn('[dataUtils] Duplicate IDs detected', {
      duplicates: [...new Set(duplicates)],
      source,
      count: duplicates.length
    });
  }
  
  return result;
}

// ============================================================================
// Conflict Resolution Helper
// ============================================================================

/**
 * Determine if existing entity should be preferred over incoming update
 * Client optimistic updates (with _clientUpdatedAt) always win over stale server data
 * @param {Object} existing - Existing entity
 * @param {Object} incoming - Incoming entity
 * @returns {boolean} True if existing should be kept
 */
export function shouldPreferClient(existing, incoming) {
  if (!existing) return false;
  if (!incoming) return true;
  
  // Client optimistic writes always win
  if (existing._clientUpdatedAt) {
    // If incoming has no timestamp, keep client version
    if (!incoming.updatedAt && !incoming._clientUpdatedAt) return true;
    
    // If incoming is older than client update, keep client version
    const incomingTime = incoming._clientUpdatedAt || incoming.updatedAt;
    if (incomingTime) {
      const existingTime = new Date(existing._clientUpdatedAt).getTime();
      const incomingTimeMs = new Date(incomingTime).getTime();
      if (existingTime > incomingTimeMs) return true;
    }
  }
  
  return false;
}

// ============================================================================
// Upsert Helper (with pending writes protection)
// ============================================================================

/**
 * Upsert entities into existing object, respecting pending writes and timestamps
 * @param {Object} existingObj - Existing entities object { [id]: entity }
 * @param {Array} newItems - New items to upsert
 * @param {string} source - Source identifier for logging
 * @param {Object} pendingWritesRef - Ref object containing pendingWritesById Map
 * @returns {Object} New object with upserted entities (immutable update)
 */
export function upsertById(existingObj, newItems, source = 'unknown', pendingWritesRef = null) {
  if (!Array.isArray(newItems) || newItems.length === 0) {
    return existingObj;
  }
  
  const result = { ...existingObj };
  const pendingWrites = pendingWritesRef?.current || {};
  let skippedCount = 0;
  let updatedCount = 0;
  let conflictCount = 0;
  
  for (const item of newItems) {
    if (!item) continue;
    
    const id = normAssignmentId(item) || normEventId(item) || normDocumentId(item) || normCourseId(item);
    if (!id) continue;
    
    // Skip entities with pending writes (will reconcile after API completes)
    if (pendingWrites[id]) {
      skippedCount++;
      if (DEBUG_DATA_RACES) {
        console.debug('[dataUtils] Skipping upsert for pending write', {
          id,
          source,
          pendingSeq: pendingWrites[id].seq
        });
      }
      continue;
    }
    
    const existing = result[id];
    
    // Conflict resolution: prefer client optimistic updates
    if (existing && shouldPreferClient(existing, item)) {
      conflictCount++;
      if (DEBUG_DATA_RACES) {
        console.debug('[dataUtils] Preferring client version over incoming', {
          id,
          source,
          existingClientUpdatedAt: existing._clientUpdatedAt,
          incomingUpdatedAt: item.updatedAt
        });
      }
      continue;
    }
    
    result[id] = item;
    updatedCount++;
  }
  
  if (DEBUG_DATA_RACES && (skippedCount > 0 || conflictCount > 0)) {
    console.debug('[dataUtils] Upsert summary', {
      source,
      updated: updatedCount,
      skipped: skippedCount,
      conflicts: conflictCount,
      total: newItems.length
    });
  }
  
  return result;
}

// ============================================================================
// Schema Normalization for Create Flows
// ============================================================================

/**
 * NORMALIZATION BOUNDARY
 * ----------------------
 * These normalizers guarantee schema completeness ONLY for create flows.
 * They must NOT:
 *  - compute derived or display fields (courseCode, courseName, etc.)
 *  - import endeavor/course data
 *  - perform lookups or formatting
 *
 * Derived fields (e.g. courseCode) belong in UI components
 * via helpers like getCourseCode(endeavorId).
 *
 * Purpose: Ensure API responses from create endpoints have all fields
 * required by Timeline filtering and rendering logic, preventing "thin"
 * objects from being filtered out before they appear in the UI.
 */

/**
 * Normalize assignment from create API response
 * Merges API response with submitted payload and fills required schema defaults
 * 
 * @param {Object} apiResponse - Raw response from createAssignment API
 * @param {Object} payload - Original submitted assignment data
 * @returns {Object} Fully normalized assignment object
 */
export function normalizeAssignment(apiResponse, payload) {
  if (!apiResponse) return null;
  
  // Merge API response with payload to ensure completeness
  const merged = { ...payload, ...apiResponse };
  
  // Normalize required ID fields
  const assignmentId = normAssignmentId(merged);
  const endeavorId = normEndeavorId(merged);
  
  // Ensure all filter-critical fields exist
  return {
    ...merged,
    // Canonical ID fields
    assignmentId: assignmentId ?? merged.id,
    id: assignmentId ?? merged.id,
    endeavorId: endeavorId,
    
    // Required string fields
    title: merged.title || merged.name || 'Untitled Assignment',
    description: merged.description || '',
    
    // Required date field for Timeline filtering
    dueDatetime: normDueDate(merged) || merged.dueDatetime,
    
    // Required flag fields with defaults
    completed: merged.completed ?? false,
    
    // Type and priority fields
    assignmentType: merged.assignmentType || 'assignment',
    priority: merged.priority ?? 'medium',
    
    // Optional fields
    points: merged.points ?? null,
    eventId: merged.eventId ?? null,
    userId: merged.userId ?? null,
    
    // Timestamps (preserve from API or use current time as fallback)
    createdAt: merged.createdAt || merged.created_at || new Date().toISOString(),
    updatedAt: merged.updatedAt || merged.updated_at || new Date().toISOString()
  };
}

/**
 * Normalize event from create API response
 * Handles both single event objects and arrays of expanded recurring instances
 * 
 * @param {Object|Array} apiResponse - Raw response from createEvent API (single object or array)
 * @param {Object} payload - Original submitted event data
 * @returns {Object|Array} Fully normalized event object(s)
 * 
 * RECURRING EVENTS:
 * - Model A (rule-based): API returns single object with recurrenceRule
 *   → Returns single normalized object with rule intact
 * - Model B (expanded): API returns array of concrete event instances
 *   → Returns array of normalized objects
 * 
 * This normalizer NEVER expands recurrence rules - expansion is Timeline's responsibility
 */
export function normalizeEvent(apiResponse, payload) {
  if (!apiResponse) return null;
  
  // Handle array responses (Model B: backend-expanded recurring events)
  if (Array.isArray(apiResponse)) {
    return apiResponse.map(event => normalizeSingleEvent(event, payload));
  }
  
  // Handle single object response (Model A: rule-based or non-recurring)
  return normalizeSingleEvent(apiResponse, payload);
}

/**
 * Internal helper to normalize a single event object
 * @private
 */
function normalizeSingleEvent(apiResponse, payload) {
  if (!apiResponse) return null;
  
  // Merge API response with payload to ensure completeness
  const merged = { ...payload, ...apiResponse };
  
  // Normalize required ID fields
  const eventId = normEventId(merged);
  const endeavorId = normEndeavorId(merged);
  
  // Ensure all filter-critical fields exist
  return {
    ...merged,
    // Canonical ID fields
    eventId: eventId ?? merged.id,
    id: eventId ?? merged.id,
    endeavorId: endeavorId,
    
    // Required string fields
    name: merged.name || merged.title || 'Untitled Event',
    title: merged.title || merged.name || 'Untitled Event',
    description: merged.description || '',
    
    // Required date fields for Timeline filtering
    startDatetime: normStartDate(merged) || merged.startDatetime,
    endDatetime: merged.endDatetime || merged.endDateTime || merged.end_datetime,
    
    // Type field
    eventType: merged.eventType || merged.event_type || 'event',
    
    // Optional fields
    location: merged.location ?? null,
    userId: merged.userId ?? null,
    
    // Recurrence fields (preserve without expansion)
    isRecurring: merged.isRecurring ?? false,
    recurrenceRule: merged.recurrenceRule ?? null,
    parentEventId: merged.parentEventId ?? null,
    
    // Timestamps (preserve from API or use current time as fallback)
    createdAt: merged.createdAt || merged.created_at || new Date().toISOString(),
    updatedAt: merged.updatedAt || merged.updated_at || new Date().toISOString()
  };
}
